import {
    Button,
    createStyles,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    Theme,
    Typography,
    withStyles,
    WithStyles
} from "@material-ui/core";
import { AddCircleOutline, Publish, OpenInNew } from "@material-ui/icons";
import "moment-timezone";
import React, { useEffect } from "react";
import Moment from "react-moment";
import { connect } from "react-redux";
import { RouteComponentProps, useHistory } from "react-router";
import { Link, LinkProps } from "react-router-dom";
import { bindActionCreators } from "redux";
import IAuction from "../../../models/auction/IAuction";
import { LoadState } from "../../../models/utility/LoadState";
import { IAppDispatch, IAppState } from "../../../store";
import * as AuctionStore from "../../../store/AuctionStore";
import FullscreenLoader from "../../layout/FullscreenLoader";
import SectionBanner from "../../shared/SectionBanner";
import Constants from "./../../../models/utility/Constants";
import {
    sortAuctionByDateDescending,
    sortAuctionByLive
} from "./../../../models/utility/Sorting";
import { enqueueSnackbar } from "notistack";
import AuctionApi from "../../../api/AuctionApi";

const styles = (theme: Theme) =>
    createStyles({
        buttonCreateNew: { float: "right" },
        editButton: { padding: theme.spacing() },
        noAuctionsText: { margin: theme.spacing(3) },
        paper: {
            alignItems: "center",
            display: "flex",
            flexDirection: "column"
        },
        section: {
            [theme.breakpoints.down("xs")]: {
                paddingLeft: theme.bodyContent.mobilePadding,
                paddingRight: theme.bodyContent.mobilePadding
            },
            backgroundColor: theme.palette.background.paper,
            paddingLeft: theme.bodyContent.desktopPadding,
            paddingRight: theme.bodyContent.desktopPadding,
            position: "relative",
            width: "100%"
        },
        sectionContent: {
            [theme.breakpoints.down("xs")]: {
                paddingBottom: theme.bodyContent.mobilePadding,
                paddingTop: theme.bodyContent.mobilePadding
            },
            margin: "0 auto",
            maxWidth: theme.bodyContent.maxWidth,
            paddingBottom: theme.bodyContent.desktopPadding,
            paddingTop: theme.bodyContent.desktopPadding,
            width: "100%"
        },
        statusLabel: {
            display: "inline-block",
            padding: `3px 6px`
        },
        statusLabelClosed: {
            backgroundColor: theme.palette.error.main,
            color: theme.palette.error.contrastText
        },
        statusLabelDraft: {
            backgroundColor: theme.palette.grey[400],
            color: theme.palette.common.white
        },
        statusLabelLive: {
            backgroundColor: theme.palette.success.main,
            color: theme.palette.success.contrastText
        },
        statusLabelUpcoming: {
            backgroundColor: theme.palette.primary.main,
            color: theme.palette.primary.contrastText
        },
        table: {
            marginBottom: theme.spacing(5),
            width: "100%"
        },
        tableCell: { borderColor: theme.palette.divider },
        tableCellButton: {
            borderColor: theme.palette.divider,
            paddingLeft: theme.spacing(),
            paddingRight: 0
        },
        tableCellHeader: { borderColor: "transparent" },
        tableHeader: { fontWeight: "bold" },
        tableLast: { marginBottom: 0 },
        tableRow: {
            "&:nth-of-type(odd)": {
                backgroundColor: theme.palette.background.default
            },
            "&:hover": {
                backgroundColor: theme.palette.primary.light,
                cursor: "pointer"
            },
            borderColor: theme.palette.grey[500]
        },
        buttonToolbar: {
            float: "right",
            "& > *": {
                marginRight: theme.spacing(2), // "16px",
                "&:last-child": {
                    marginRight: "0"
                }
            }
        }
    });

type ManageAuctionListProps = AuctionStore.IAuctionStoreState &
    typeof AuctionStore.actionCreators &
    WithStyles<typeof styles> &
    RouteComponentProps;

function ManageAuctionList({
    auctions,
    auctionsLoadState,
    classes,
    loadAuctionList
}: ManageAuctionListProps) {
    const history = useHistory();

    useEffect(() => {
        Constants.SetDocumentTitle("Admin - Manage Auctions");
    }, []);

    useEffect(() => {
        loadAuctionList(true);
    }, [loadAuctionList]);

    const onClickPublishButton = async () => {
        const errors = await AuctionApi.PublishAuctions();

        if (errors?.length > 0) {
            enqueueSnackbar(errors[0].description, {
                anchorOrigin: { horizontal: "left", vertical: "top" },
                preventDuplicate: false,
                variant: "error",
                persist: false
            });
        } else {
            enqueueSnackbar(
                'Publishing started. Click "View Deploys" to see progress..',
                {
                    anchorOrigin: { horizontal: "left", vertical: "top" },
                    preventDuplicate: false,
                    variant: "success",
                    persist: false
                }
            );
        }
    };

    const renderAuctionStatus = (auction: IAuction) => {
        let labelClasses = classes.statusLabel;
        let labelText = "";

        if (auction.isCompleted) {
            labelClasses += ` ${classes.statusLabelClosed}`;
            labelText = "Closed";
        } else if (auction.isLive) {
            labelClasses += ` ${classes.statusLabelLive}`;
            labelText = "LIVE!";
        } else if (auction.draftMode) {
            labelClasses += ` ${classes.statusLabelDraft}`;
            labelText = "Draft";
        } else {
            labelClasses += ` ${classes.statusLabelUpcoming}`;
            labelText = "Upcoming";
        }

        return <div className={labelClasses}>{labelText}</div>;
    };

    const renderAuctionRow = (auction: IAuction) => {
        return (
            <TableRow
                key={auction.auctionId}
                className={classes.tableRow}
                onClick={() => handleClick(auction.url)}
            >
                <TableCell className={classes.tableCell}>
                    {auction.name}
                </TableCell>
                <TableCell className={classes.tableCell}>
                    {auction.url}
                </TableCell>
                <TableCell className={classes.tableCell}>
                    {auction.auctionDate === Constants.UnscheduledTime ? (
                        <Typography color="textPrimary">Unscheduled</Typography>
                    ) : (
                        <Moment
                            date={auction.auctionDate}
                            format={Constants.DateFormat}
                            tz={Constants.TimeZone}
                        />
                    )}
                </TableCell>
                <TableCell className={classes.tableCell}>
                    {auction.parcelCount || 0}
                </TableCell>
                <TableCell className={classes.tableCell}>
                    {renderAuctionStatus(auction)}
                </TableCell>
            </TableRow>
        );
    };

    const auctionCreateLink = (props: Partial<LinkProps>) => (
        <Link
            to={Constants.Routes.AdminAuctionDetail(Constants.UrlCreateAuction)}
            {...props}
        />
    );

    const viewDeploysLink = (props: Partial<LinkProps>) => (
        <Link
            target="_blank"
            to={Constants.Routes.AdminAuctionDetail(Constants.ViewDeploys)}
            {...props}
        />
    );

    const viewRemindersLink = (props: Partial<LinkProps>) => (
        <Link
            target="_blank"
            to={Constants.Routes.AdminAuctionDetail(Constants.ViewReminders)}
            {...props}
        />
    );

    const handleClick = (auctionUrl: string) => {
        history.push(Constants.Routes.AdminAuctionDetail(auctionUrl));
    };

    if (auctionsLoadState !== LoadState.Loaded) {
        return <FullscreenLoader />;
    }

    const onSale = auctions
        .filter((auction) => !auction.isCompleted)
        .sort(sortAuctionByLive);
    const pastSales = auctions
        .filter((auction) => auction.isCompleted)
        .sort(sortAuctionByDateDescending);

    return (
        <Paper className={classes.paper} elevation={2}>
            <SectionBanner leftSideText="Admin - Manage Auctions" />
            <div className={classes.section}>
                <div className={classes.sectionContent}>
                    {/* Upcoming Auctions */}
                    <Paper>
                        <Table className={classes.table}>
                            <TableHead>
                                <TableRow>
                                    <TableCell
                                        className={classes.tableCellHeader}
                                        colSpan={2}
                                    >
                                        <Typography
                                            className={classes.tableHeader}
                                            variant="subtitle1"
                                        >
                                            Upcoming/Live Auctions
                                        </Typography>
                                    </TableCell>
                                    <TableCell colSpan={3}>
                                        <div className={classes.buttonToolbar}>
                                            <Button
                                                size="small"
                                                variant="outlined"
                                                color="default"
                                                component={viewRemindersLink}
                                            >
                                                <OpenInNew />
                                                &nbsp;View Reminders
                                            </Button>
                                            <Button
                                                size="small"
                                                variant="outlined"
                                                color="default"
                                                component={viewDeploysLink}
                                            >
                                                <OpenInNew />
                                                &nbsp;View Deploys
                                            </Button>
                                            <Button
                                                size="small"
                                                variant="outlined"
                                                color="default"
                                                onClick={onClickPublishButton}
                                            >
                                                <Publish />
                                                &nbsp;Publish
                                            </Button>
                                            <Button
                                                size="small"
                                                variant="outlined"
                                                color="default"
                                                component={auctionCreateLink}
                                            >
                                                <AddCircleOutline />
                                                &nbsp;Create New
                                            </Button>
                                        </div>
                                    </TableCell>
                                </TableRow>
                                <TableRow>
                                    <TableCell className={classes.tableCell}>
                                        Name
                                    </TableCell>
                                    <TableCell className={classes.tableCell}>
                                        URL
                                    </TableCell>
                                    <TableCell className={classes.tableCell}>
                                        Start Date
                                    </TableCell>
                                    <TableCell className={classes.tableCell}>
                                        Parcels
                                    </TableCell>
                                    <TableCell className={classes.tableCell}>
                                        Status
                                    </TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {onSale.length === 0 && (
                                    <TableCell colSpan={6}>
                                        <Typography
                                            className={classes.noAuctionsText}
                                            component="div"
                                            variant="body2"
                                            align="center"
                                            color="textSecondary"
                                        >
                                            No upcoming/live auctions to
                                            display.
                                        </Typography>
                                    </TableCell>
                                )}
                                {onSale.length > 0 &&
                                    onSale.map((auction) =>
                                        renderAuctionRow(auction)
                                    )}
                            </TableBody>
                        </Table>
                    </Paper>

                    {/* Closed Auctions */}
                    <Paper>
                        <Table
                            className={`${classes.table} ${classes.tableLast}`}
                        >
                            <TableHead>
                                <TableRow>
                                    <TableCell
                                        className={classes.tableCellHeader}
                                        colSpan={6}
                                    >
                                        <Typography
                                            className={classes.tableHeader}
                                            variant="subtitle1"
                                        >
                                            Closed Auctions
                                        </Typography>
                                    </TableCell>
                                </TableRow>
                                <TableRow>
                                    <TableCell className={classes.tableCell}>
                                        Name
                                    </TableCell>
                                    <TableCell className={classes.tableCell}>
                                        URL
                                    </TableCell>
                                    <TableCell className={classes.tableCell}>
                                        Start Date
                                    </TableCell>
                                    <TableCell className={classes.tableCell}>
                                        Parcels
                                    </TableCell>
                                    <TableCell className={classes.tableCell}>
                                        Status
                                    </TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {pastSales.length === 0 && (
                                    <TableCell colSpan={6}>
                                        <Typography
                                            className={classes.noAuctionsText}
                                            component="div"
                                            variant="body2"
                                            align="center"
                                            color="textSecondary"
                                        >
                                            No closed auctions to display.
                                        </Typography>
                                    </TableCell>
                                )}
                                {pastSales.length > 0 &&
                                    pastSales.map((auction) =>
                                        renderAuctionRow(auction)
                                    )}
                            </TableBody>
                        </Table>
                    </Paper>
                </div>
            </div>
        </Paper>
    );
}

const reduxState = (state: IAppState) =>
    Object.assign({}, state.auctionStoreState);
const reduxDispatch = (dispatch: IAppDispatch) =>
    bindActionCreators(
        Object.assign({}, AuctionStore.actionCreators),
        dispatch
    );

export default withStyles(styles)(
    connect(reduxState, reduxDispatch)(ManageAuctionList)
);
